// The current stage, for a deal writer (TX-US-0043): what the next move waits on, each item ticked
// when the deal has it, the move, labelled by what it does and offered once nothing is missing,
// and beside it Withdraw, Reopen and Cancel. The API decides - the items and whether each is
// present are its own (`next_move.needed`) - and this says why it refused.

import { useState } from 'react';

import { moveDeal } from '../api.js';
import { notify } from '../ui/notify.jsx';
import { STAGE_LABELS, neededLabel, offeredMoves, refusalOf, waitsOnDocument } from './flow.js';
import ReasonDialog from './ReasonDialog.jsx';

const REASONED = {
  REOPEN: {
    title: 'Reopen the proposal?',
    hint: 'The deal goes back to Proposal, where its terms are edited again, and it needs a new '
      + 'round of approval.',
    confirmLabel: 'Reopen',
    busyLabel: 'Reopening…',
  },
  CANCEL: {
    title: 'Cancel the deal?',
    hint: 'A cancelled deal is final: it takes no edit and no move. Documents can still be added.',
    confirmLabel: 'Cancel the deal',
    busyLabel: 'Cancelling…',
    danger: true,
  },
};

// `namedPlace` is the named place's label under the deal's Incoterm. A move that succeeds leaves
// the panel busy: the deal is read again, and the panel comes back for its new version.
//
// `unsaved` holds the forward move back while the page's form has changes not yet saved: what the
// move waits on is read from the saved deal. Withdraw, Reopen and Cancel do not read the fields.
export default function StagePanel({ deal, view, me, namedPlace, onChanged, unsaved = false }) {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [asking, setAsking] = useState(null);
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false);

  const offered = offeredMoves(deal, me);
  const needed = view.next_move?.needed ?? [];
  const ready = needed.every((item) => item.present);
  // A refusal belongs to the view it was made against: a newer view - after a document arrives,
  // say - says afresh what the move waits on.
  const refused = refusal?.view === view ? refusal : null;

  const move = async (name, reason = null) => {
    const { deal: updated } = await moveDeal(deal.pre_deal_id, name, deal.version, reason);
    setAsking(null);
    // Every control stays disabled until the deal is read again and the panel comes back for
    // its new version - a dialog's move as much as a button's.
    setBusy(true);
    notify(`Deal moved to ${STAGE_LABELS[updated.stage]}.`);
    onChanged();
  };

  const moveNow = async (name) => {
    setBusy(true);
    setRefusal(null);
    try {
      await move(name);
    } catch (err) {
      setRefusal({ ...refusalOf(err, namedPlace), view });
      setBusy(false);
    }
  };

  // Two presses: the first arms the button, the second withdraws.
  const withdraw = () => {
    if (!confirmingWithdraw) {
      setConfirmingWithdraw(true);
      return;
    }
    setConfirmingWithdraw(false);
    moveNow('WITHDRAW');
  };

  return (
    <section className="profile-panel stage-panel" aria-label="Moving the deal on">
      {view.next_move && (
        <div className="move-needs">
          <h4>Needed to move on</h4>
          {needed.length > 0 ? (
            <ul>
              {needed.map((item) => (
                <li key={item.key} className={item.present ? 'present' : 'missing'}>
                  <span aria-hidden="true">{item.present ? '✓' : '✗'}</span>{' '}
                  {neededLabel(item.key, namedPlace)}
                  <span className="visually-hidden">{item.present ? ', present' : ', missing'}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ready"><span aria-hidden="true">✓</span> Nothing — ready to move on</p>
          )}
          {waitsOnDocument(needed) && (
            <p className="hint">
              Add a missing document under{' '}
              <a href={`#documents-${deal.pre_deal_id}`}>Documents</a> below: choose its type,
              then drop the file.
            </p>
          )}
        </div>
      )}

      {deal.stage === 'APPROVAL' && deal.approval && (
        <p className="hint">
          Round {deal.approval.round}, submitted by {deal.approval.submitted_by}: waiting for
          the {deal.approval.commercial ? 'risk' : 'commercial'} step. Nothing on the deal is
          edited until it is decided{offered.withdraw ? ', or you withdraw it' : ''}.
        </p>
      )}

      <div className="status-actions-row">
        {offered.forward && (
          <button type="button" className="primary" disabled={busy || !ready || unsaved}
                  onClick={() => moveNow(offered.forward.move)}>
            {busy ? 'Moving…' : offered.forward.label}
          </button>
        )}
        {offered.forward && unsaved && (
          <span className="hint">Save or discard your changes first.</span>
        )}
        {offered.withdraw && (
          <button type="button" className={confirmingWithdraw ? 'confirming' : undefined}
                  disabled={busy} onClick={withdraw} onBlur={() => setConfirmingWithdraw(false)}
                  title={confirmingWithdraw
                    ? 'Click again to withdraw: the deal goes back to Proposal'
                    : undefined}>
            {confirmingWithdraw ? 'Confirm withdraw' : 'Withdraw'}
          </button>
        )}
        {offered.reopen && (
          <button type="button" disabled={busy} onClick={() => setAsking('REOPEN')}>
            Reopen proposal
          </button>
        )}
        {offered.cancel && (
          <button type="button" className="danger" disabled={busy}
                  onClick={() => setAsking('CANCEL')}>
            Cancel deal
          </button>
        )}
      </div>

      {refused && (
        <p className="error">
          {refused.message}
          {refused.stale && (
            <> <button type="button" className="link-button" onClick={onChanged}>Reload</button></>
          )}
        </p>
      )}

      {asking && (
        <ReasonDialog {...REASONED[asking]} onClose={() => setAsking(null)}
                      act={(reason) => move(asking, reason)}
                      refusal={(err) => refusalOf(err, namedPlace).message} />
      )}
    </section>
  );
}
